<template>
  <div>
    <ul
      ref="ul"
      :style="{ height: height, overflow: overflow }"
      class="collapsable-lnk-list collapsable-lnk-list--animated"
    >
      <li
        v-for="(item, ix) in items"
        :key="item.key || item.name"
        :ref="ix === items.length - 1 ? 'lastLi' : ix === showMoreStartIx ? 'li7' : undefined"
        :class="liClasses(ix)"
      >
        <div class="collapsable-lnk-list__action-wrapper">
          <cdr-button
            v-if="!!item.children.length"
            :icon-only="true"
            :aria-label="
              expandedItems[item.name] ? `Collapse ${item.label}` : `Expand ${item.label}`
            "
            :tabindex="!parentExpanded && -1"
            class="collapsable-lnk-list__button"
            @click="toggleItem(filterName, item.name)"
          >
            <icon-minus v-if="expandedItems[item.name]" />
            <icon-plus v-else />
          </cdr-button>
          <cdr-link
            v-bind="item.link.attrs"
            :class="linkClasses(item)"
            :tabindex="!parentExpanded && -1"
            modifier="standalone"
            v-on="item.link.on"
          >
            {{ item.label }}
          </cdr-link>
        </div>
        <collapsable-filter-link-list
          v-if="!!item.children.length"
          :style="(!parentExpanded || !expandedItems[item.name]) && { display: 'none' }"
          :filter-name="filterName"
          :toggle-item="toggleItem"
          :expanded-items="expandedItems"
          :parent-expanded="!!expandedItems[item.name]"
          :items="item.children"
          :use-show-more="false"
          class="collapsable-lnk-list--children"
        />
      </li>
    </ul>
    <cdr-link
      v-if="useShowMore && items.length > maxLength"
      :class="['filters--mobile-hidden', 'filters__show-more']"
      :tabindex="parentExpanded ? 0 : -1"
      tag="button"
      modifier="standalone"
      @click="toggleShowMore"
    >
      {{ showMoreContent }}
    </cdr-link>
  </div>
</template>

<script setup>
import { CdrButton, CdrLink, IconPlusSm as IconPlus, IconMinusSm as IconMinus } from '@rei/cedar';
import { computed, ref } from 'vue';
import useFilters from '../../composables/search/filters';

const ul = ref(null);
const lastLi = ref([]);
const li7 = ref([]);

defineOptions({
  name: 'CollapsableFilterLinkList',
});

const props = defineProps({
  maxLength: {
    type: Number,
    default: 10,
  },
  showMoreStartIx: {
    type: Number,
    default: 6,
  },
  filterName: {
    type: String,
    required: true,
  },
  parentExpanded: {
    type: Boolean,
    required: true,
  },
  toggleItem: {
    type: Function,
    required: true,
  },
  expandedItems: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  useShowMore: {
    type: Boolean,
    default: true,
  },
  showMoreText: {
    type: String,
    default: null,
  },
  showLessText: {
    type: String,
    default: null,
  },
});

const { moreShown, collapsing, height, overflow } = useFilters({ lastLi, li7, ul });

function liClasses(itemIx) {
  const { items, maxLength, showMoreStartIx } = props;
  const hidden =
    items.length > maxLength && itemIx > showMoreStartIx && !moreShown.value && !collapsing.value;

  return ['collapsable-lnk-list__item', { 'collapsable-lnk-list--mobile-shown': hidden }];
}

function linkClasses(link) {
  const { items } = props;
  const siblingHasButton = !!items.find((item) => item.children.length) && !link.children.length;

  return [
    'collapsable-lnk-list__anchor',
    {
      'collapsable-lnk-list__anchor--no-button': siblingHasButton,
      'collapsable-lnk-list__anchor--selected': link.selected,
    },
  ];
}

const showMoreContent = computed(() => {
  if (moreShown.value) {
    return props.showLessText || 'Show Less';
  }
  return props.showMoreText || 'Show All';
});
</script>

<style lang="scss" scoped>
@use '@/style/components/search/collapsable-filter-link-list-component.scss';
</style>
